import type { SttTranscribeResult } from 'src/pages/chat/components/stt/types/stt-transcribe.types';
import type { TtsSynthesizeRequest } from 'src/pages/chat/components/tts/types/tts-synthesize.types';

import { parseTtsSynthesizeResponse } from 'src/pages/chat/components/tts/utils/tts-synthesize-client';

import { settingApi } from '../../../slice/settingApi';

import type { TenantAISettings } from './aiSettingsSlice';

const AI_SETTINGS_API = {
  get: 'api/ai-settings',
  update: 'api/ai-settings',
  providerKey: 'api/ai-settings/provider-key',
  providerKeyStatus: 'api/ai-settings/provider-key-status',
  tokenSummary: 'api/ai-analytics/token-summary',
  ttsSynthesize: 'api/ai-settings/tts/synthesize',
  sttTranscribe: 'api/ai-settings/stt/transcribe',
};

export interface ProviderKeyStatus {
  openai: { configured: boolean };
  gemini: { configured: boolean };
  claude: { configured: boolean };
}

export interface TokenSummary {
  today: {
    total: number;
    agentAssist: number;
    chatbot: number;
    voicebot: number;
    sentiment: number;
  };
  week: { total: number };
}

export const aiSettingsApi = settingApi.injectEndpoints({
  endpoints: (builder) => ({
    getAISettings: builder.query<TenantAISettings, void>({
      query: () => ({
        url: AI_SETTINGS_API.get,
        method: 'GET',
      }),
      transformResponse: (response: { data?: TenantAISettings }) =>
        response?.data as TenantAISettings,
      providesTags: ['TenantConfig'],
    }),

    updateAISettings: builder.mutation<TenantAISettings, Partial<TenantAISettings>>({
      query: (body) => ({
        url: AI_SETTINGS_API.update,
        method: 'PUT',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response: { data?: TenantAISettings }) =>
        response?.data as TenantAISettings,
      invalidatesTags: ['TenantConfig'],
    }),

    getProviderKeyStatus: builder.query<ProviderKeyStatus, void>({
      query: () => ({
        url: AI_SETTINGS_API.providerKeyStatus,
        method: 'GET',
      }),
      transformResponse: (response: { data?: ProviderKeyStatus }) =>
        response?.data as ProviderKeyStatus,
      providesTags: ['ProviderKeyStatus'],
    }),

    setProviderKey: builder.mutation<
      { provider: string; keyStored: boolean },
      { provider: 'openai' | 'gemini' | 'claude'; apiKey: string }
    >({
      query: (body) => ({
        url: AI_SETTINGS_API.providerKey,
        method: 'PUT',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response: { data?: any }) => response?.data,
      invalidatesTags: ['ProviderKeyStatus'],
    }),

    getTokenSummary: builder.query<TokenSummary, void>({
      query: () => ({
        url: AI_SETTINGS_API.tokenSummary,
        method: 'GET',
      }),
      transformResponse: (response: { data?: TokenSummary }) =>
        response?.data as TokenSummary,
    }),

    getAvailableModels: builder.query<
      { id: string; name: string }[],
      'openai' | 'gemini' | 'claude'
    >({
      query: (provider) => ({
        url: `${AI_SETTINGS_API.get}/available-models?provider=${provider}`,
        method: 'GET',
      }),
      transformResponse: (response: { data?: { models: { id: string; name: string }[] } }) =>
        response?.data?.models ?? [],
    }),

    /** Tenant TTS (Google Cloud / OpenAI) — returns audio bytes or JSON base64. */
    synthesizeTts: builder.mutation<Blob, TtsSynthesizeRequest>({
      query: (body) => ({
        url: AI_SETTINGS_API.ttsSynthesize,
        method: 'POST',
        body: { ...body, format: body.format ?? 'mp3' },
        responseHandler: (response) => parseTtsSynthesizeResponse(response),
      }),
    }),

    transcribeStt: builder.mutation<{ text: string; confidence: number | null }, FormData>({
      query: (formData) => ({
        url: AI_SETTINGS_API.sttTranscribe,
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response: { data?: SttTranscribeResult }) => {
        const payload = response?.data ?? (response as unknown as SttTranscribeResult);
        const text = (payload?.transcript ?? payload?.text ?? '').trim();
        const confidence = typeof payload?.confidence === 'number' ? payload.confidence : null;
        return { text, confidence };
      },
    }),
  }),
});

export const {
  useGetAISettingsQuery,
  useUpdateAISettingsMutation,
  useGetProviderKeyStatusQuery,
  useSetProviderKeyMutation,
  useGetTokenSummaryQuery,
  useGetAvailableModelsQuery,
  useSynthesizeTtsMutation,
  useTranscribeSttMutation,
} = aiSettingsApi;
